// Informativa privacy — versione italiana, autoritativa (vedi AUTHORITATIVE_LANG).
//
// The five translations share this exact structure: same section `id`s, same order, same
// block types. `privacy.test.js` enforces that — a section that exists only in Italian is a
// section four fifths of the users never see.
//
// Block types understood by the renderer (@/pages/PrivacyPolicy):
//   { p }      paragraph            { ul }     bullet list
//   { note }   highlighted callout  { table }  { head: [], rows: [[]] }
import { CONTROLLER, controllerAddressLine, hasPostalAddress } from "@/lib/legal";

// Il paese nella lingua di chi legge: CONTROLLER.country resta il valore postale.
const COUNTRY = "Italia";

const identity = [
  ["Titolare", CONTROLLER.legalName],
  CONTROLLER.tradeName && ["Nome commerciale", CONTROLLER.tradeName],
  ["Forma giuridica", "Impresa individuale (ditta individuale) di diritto italiano"],
  ["Paese di stabilimento", COUNTRY],
  CONTROLLER.vat && ["Partita IVA", CONTROLLER.vat],
  CONTROLLER.taxCode && ["Codice fiscale", CONTROLLER.taxCode],
  CONTROLLER.reaNumber && ["Iscrizione REA", CONTROLLER.reaNumber],
  hasPostalAddress() && ["Sede", controllerAddressLine()],
  ["Email", CONTROLLER.email],
  CONTROLLER.pec && ["PEC", CONTROLLER.pec],
  CONTROLLER.phone && ["Telefono", CONTROLLER.phone],
].filter(Boolean);

const doc = {
  title: "Informativa sul trattamento dei dati personali",
  subtitle:
    "Ai sensi degli articoli 13 e 14 del Regolamento (UE) 2016/679 (GDPR) e del D.lgs. 196/2003 come modificato dal D.lgs. 101/2018.",
  updatedLabel: "Ultimo aggiornamento",
  versionLabel: "Versione",
  tocLabel: "Indice",
  authoritativeNote: null, // la versione italiana è quella che prevale
  backLabel: "Torna al sito",

  sections: [
    {
      id: "titolare",
      h: "1. Chi tratta i tuoi dati",
      blocks: [
        {
          p: `Il titolare del trattamento è ${CONTROLLER.legalName}, impresa individuale ${
            hasPostalAddress() ? `con sede in ${controllerAddressLine()}` : `stabilita in ${COUNTRY}`
          }, che gestisce il servizio VisioScreening (di seguito anche “il Servizio”, raggiungibile su visioscreening.com).`,
        },
        {
          p: `Il titolare è raggiungibile all'indirizzo ${CONTROLLER.email} per ogni comunicazione relativa alla presente informativa, incluse le richieste di esercizio dei diritti descritte alla sezione 10. Chi necessiti dei dati identificativi completi del titolare — inclusi partita IVA e sede — può richiederli allo stesso indirizzo e li riceve senza oneri.`,
        },
        { table: { head: ["", ""], rows: identity } },
        {
          p: "Non è stato nominato un Responsabile della protezione dei dati (DPO): il trattamento non rientra nei casi previsti dall'art. 37 del GDPR, poiché non consiste in un monitoraggio regolare e sistematico su larga scala né nel trattamento su larga scala di categorie particolari di dati. Per qualunque questione relativa ai dati personali puoi scrivere direttamente agli indirizzi indicati sopra.",
        },
      ],
    },

    {
      id: "ruoli",
      h: "2. Due tipi di dati, due ruoli diversi",
      blocks: [
        {
          p: "VisioScreening è uno strumento professionale: chi lo usa è un medico, un ottico, un tecnico della prevenzione o un altro professionista sanitario che somministra test visivi a un lavoratore videoterminalista. Questo produce due categorie di dati completamente separate, che è essenziale non confondere.",
        },
        {
          table: {
            head: ["Dati", "Chi è il titolare", "Dove si trovano"],
            rows: [
              [
                "Dati del professionista che usa il Servizio (account, struttura, pagamento)",
                `${CONTROLLER.legalName} è titolare del trattamento`,
                "Sui server del Servizio",
              ],
              [
                "Dati del lavoratore sottoposto ai test (anagrafica, risposte, esiti, referto)",
                "Il professionista o la struttura sanitaria è titolare autonomo",
                "Esclusivamente sul dispositivo del professionista",
              ],
            ],
          },
        },
        {
          note: "I dati clinici del lavoratore non vengono mai inviati ai nostri server. Restano nella memoria del browser per la durata dello screening e il referto PDF viene generato localmente, sul dispositivo del professionista. Su questi dati non siamo né titolari né responsabili del trattamento, perché non ne veniamo mai in possesso. La sezione 4 spiega nel dettaglio come funziona.",
        },
      ],
    },

    {
      id: "dati",
      h: "3. Quali dati trattiamo e perché",
      blocks: [
        {
          p: "Trattiamo solo i dati necessari a farti avere un account, a farti generare i referti e a incassare il pagamento. Non facciamo profilazione, non inviamo newsletter senza richiesta, non cediamo dati a terzi per finalità di marketing e non usiamo strumenti di analisi del comportamento.",
        },
        {
          table: {
            head: ["Categoria di dati", "Finalità", "Base giuridica", "Conservazione"],
            rows: [
              [
                "Dati dell'account: indirizzo email, nome (facoltativo), password conservata solo come hash bcrypt; se accedi con Google, nome, email e immagine del profilo trasmessi da Google",
                "Creare e gestire l'account, autenticarti, permetterti di recuperare la password",
                "Art. 6(1)(b) GDPR — esecuzione del contratto",
                "Per tutta la durata dell'account e fino a 30 giorni dalla richiesta di cancellazione (backup fino a 90 giorni)",
              ],
              [
                "Dati della struttura: denominazione e indirizzo della struttura, nome del medico, logo e immagine della firma che carichi",
                "Personalizzare l'intestazione del referto PDF con i dati del tuo studio",
                "Art. 6(1)(b) GDPR — esecuzione del contratto",
                "Fino alla cancellazione dell'account o alla modifica della configurazione",
              ],
              [
                "Contatore dei referti: identificativo casuale del referto, data e ora, lingua, se il referto era gratuito o a pagamento, hash SHA-256 salato dell'indirizzo IP",
                "Far valere il diritto d'uso acquistato, contare i referti gratuiti già usati, individuare abusi del referto gratuito (creazione seriale di account)",
                "Art. 6(1)(b) e art. 6(1)(f) GDPR — esecuzione del contratto e legittimo interesse a prevenire abusi",
                "La riga resta per tutta la durata dell'account, perché è la prova del diritto d'uso acquistato; l'hash dell'IP viene rimosso una volta esaurita la finestra di pochi giorni in cui serve a individuare abusi",
              ],
              [
                "Dati di pagamento: identificativo della sessione di pagamento, importo, valuta, stato, dati di fatturazione e fiscali che inserisci su Stripe",
                "Incassare il corrispettivo una tantum, emettere e conservare i documenti contabili",
                "Art. 6(1)(b) GDPR — esecuzione del contratto; art. 6(1)(c) GDPR — obblighi fiscali e contabili",
                "I documenti contabili 10 anni dalla registrazione (art. 2220 c.c. e normativa fiscale); i dati tecnici della transazione fino alla cancellazione dell'account",
              ],
              [
                "Dati tecnici e di sicurezza: indirizzo IP, tipo di browser, data e ora delle richieste, tentativi di accesso falliti, token di sessione e di reimpostazione password",
                "Tenere in piedi il servizio, proteggerlo da accessi non autorizzati, limitare i tentativi di forzatura delle password",
                "Art. 6(1)(f) GDPR — legittimo interesse alla sicurezza della rete e delle informazioni",
                "Tentativi di accesso: pochi minuti. Token: fino alla scadenza. Log dei fornitori di hosting: normalmente 30 giorni",
              ],
              [
                "Corrispondenza: contenuto delle email o dei messaggi che ci invii per assistenza",
                "Rispondere alle tue richieste e gestire eventuali contestazioni",
                "Art. 6(1)(b) e art. 6(1)(f) GDPR — esecuzione del contratto e legittimo interesse alla difesa dei diritti",
                "Fino a 24 mesi dall'ultimo contatto, salvo contenziosi in corso",
              ],
            ],
          },
        },
        {
          p: "Il conferimento dei dati contrassegnati come obbligatori nei moduli è necessario per attivare l'account e usare il Servizio: senza di essi non è tecnicamente possibile fornirlo. Il conferimento degli altri dati è libero e la loro mancanza non pregiudica l'uso del Servizio.",
        },
      ],
    },

    {
      id: "pazienti",
      h: "4. I dati del lavoratore sottoposto ai test",
      blocks: [
        {
          p: "Il questionario anamnestico, i dati anagrafici del lavoratore, le risposte ai sette test e il referto finale sono dati relativi alla salute, cioè categorie particolari di dati ai sensi dell'art. 9 del GDPR. Il Servizio è progettato in modo da non riceverli mai.",
        },
        {
          ul: [
            "I dati inseriti durante lo screening restano nella memoria del browser, sul dispositivo del professionista, e non vengono trasmessi ai nostri server in nessuna fase.",
            "Il referto PDF viene composto interamente nel browser: non transita da noi, non lo archiviamo e non ne conserviamo copia.",
            "Se interrompi lo screening per completare il pagamento, i dati inseriti vengono salvati temporaneamente nella sessionStorage del browser per un massimo di due ore, così da poter riprendere da dove eri rimasto. La sessionStorage viene cancellata alla chiusura del browser e il logo e la firma non vi vengono mai scritti.",
            "L'unico dato che raggiunge i nostri server quando scarichi un referto è un identificativo casuale del referto, privo di qualunque riferimento al lavoratore: serve solo a scalare il contatore dei referti disponibili.",
          ],
        },
        {
          note: "Poiché non entriamo mai in possesso dei dati sanitari del lavoratore, riteniamo che non ricorrano i presupposti di una nostra designazione a responsabile del trattamento ai sensi dell'art. 28 del GDPR. Resta inteso che, su richiesta, sottoscriviamo comunque un accordo sul trattamento dei dati o rilasciamo una dichiarazione scritta che descriva l'architettura qui illustrata: scrivici all'indirizzo indicato alla sezione 14.",
        },
        {
          p: `Resta a carico del professionista che utilizza il Servizio ogni obbligo relativo ai dati del lavoratore: individuare la base giuridica del trattamento (di norma l'art. 9(2)(h) del GDPR per la medicina del lavoro, in combinato con l'art. 41 del D.lgs. 81/2008), rendere la propria informativa all'interessato, conservare il referto in modo sicuro e rispettare il segreto professionale. ${CONTROLLER.legalName} non risponde dell'uso che il professionista fa del referto una volta generato.`,
        },
      ],
    },

    {
      id: "destinatari",
      h: "5. A chi comunichiamo i dati",
      blocks: [
        {
          p: "Non vendiamo e non cediamo i tuoi dati. Li condividiamo solo con i fornitori che rendono tecnicamente possibile il Servizio, nominati responsabili del trattamento ai sensi dell'art. 28 del GDPR e vincolati a trattarli soltanto secondo le nostre istruzioni.",
        },
        {
          table: {
            head: ["Fornitore", "Ruolo", "Dati coinvolti"],
            rows: [
              ["Stripe Payments Europe, Ltd. (Irlanda)", "Gestione dei pagamenti con carta", "Dati di fatturazione, importo, esito. I dati della carta sono trattati direttamente da Stripe e non ci vengono mai comunicati"],
              ["MongoDB, Inc. / MongoDB Atlas", "Database gestito", "Tutti i dati dell'account e della licenza"],
              ["Render Services, Inc.", "Hosting dell'applicazione lato server", "Dati trasmessi alle API e log tecnici"],
              ["Netlify, Inc.", "Hosting e CDN del sito e dell'applicazione web", "Dati di connessione (indirizzo IP, user agent)"],
              ["Fornitore di posta transazionale (Resend / provider SMTP configurato)", "Invio delle email di sistema, come il ripristino della password", "Indirizzo email e contenuto del messaggio"],
              ["Google Ireland Limited", "Accesso con Google, solo se scegli di usarlo", "Identificativo, nome, email e immagine del profilo Google"],
              ["Consulenti contabili e fiscali", "Adempimenti amministrativi e fiscali", "Dati di fatturazione"],
            ],
          },
        },
        {
          p: "I dati possono inoltre essere comunicati ad autorità pubbliche quando ciò sia imposto da un obbligo di legge o richiesto per accertare, esercitare o difendere un diritto in sede giudiziaria. L'elenco aggiornato dei responsabili del trattamento è disponibile scrivendo a " + CONTROLLER.email + ".",
        },
      ],
    },

    {
      id: "trasferimenti",
      h: "6. Trasferimenti fuori dallo Spazio economico europeo",
      blocks: [
        {
          p: "Alcuni dei fornitori elencati sopra hanno sede negli Stati Uniti o possono trattare dati al di fuori dello Spazio economico europeo. In tal caso il trasferimento avviene sulla base di una delle garanzie previste dal Capo V del GDPR: una decisione di adeguatezza della Commissione europea — in particolare l'EU-U.S. Data Privacy Framework per i fornitori che vi aderiscono — oppure le Clausole contrattuali tipo adottate dalla Commissione europea con decisione di esecuzione (UE) 2021/914, integrate ove necessario da misure supplementari.",
        },
        {
          p: "Alcuni dei servizi impiegati operano in regioni situate al di fuori dell'Unione europea, in particolare negli Stati Uniti. Puoi richiedere in qualsiasi momento, senza oneri, l'indicazione aggiornata della collocazione dei dati e copia delle garanzie adottate per ciascun fornitore scrivendo a " + CONTROLLER.email + ".",
        },
      ],
    },

    {
      id: "conservazione",
      h: "7. Per quanto tempo conserviamo i dati",
      blocks: [
        {
          p: "I tempi di conservazione sono indicati per ciascuna categoria nella tabella della sezione 3. In sintesi:",
        },
        {
          ul: [
            "I dati dell'account restano finché l'account esiste. L'applicazione non espone una funzione di autocancellazione: la cancellazione è eseguita manualmente dal titolare su richiesta inviata all'indirizzo indicato alla sezione 14, senza ingiustificato ritardo e comunque entro 30 giorni, e riguarda account, licenza, configurazione della struttura, sessioni e registri dei referti.",
            "L'hash dell'indirizzo IP serve a individuare abusi del referto gratuito su una finestra di pochi giorni. Superata quella finestra non ha più utilità e viene rimosso in occasione delle verifiche periodiche del titolare; la riga del referto a cui era associato resta, priva dell'hash, perché è la prova del diritto d'uso.",
            "I documenti contabili e fiscali connessi al pagamento sono conservati per 10 anni, come impone la legge italiana: questo obbligo prevale sulla richiesta di cancellazione e riguarda i soli documenti contabili, non l'intera cronologia tecnica del pagamento.",
            "I dati tecnici e di sicurezza hanno vita breve: i contatori dei tentativi di accesso durano pochi minuti, i token di sessione e di reimpostazione scadono da sé, i log dei fornitori di hosting seguono la rotazione stabilita dai fornitori stessi.",
            "I backup gestiti dal fornitore del database seguono la rotazione prevista dal piano in uso: la cancellazione dai sistemi attivi è immediata, quella dai backup avviene al ciclo di rotazione successivo.",
          ],
        },
        {
          note: "Il fatto che la cancellazione sia eseguita manualmente non ne riduce la portata né i termini: l'art. 17 del GDPR si esercita scrivendo al titolare, e la richiesta viene evasa entro i termini della sezione 10. Se preferisci verificare l'avvenuta cancellazione, chiedilo nella stessa email e ricevi conferma scritta.",
        },
      ],
    },

    {
      id: "cookie",
      h: "8. Cookie e strumenti di memorizzazione locale",
      blocks: [
        {
          p: "VisioScreening non utilizza cookie di profilazione, cookie pubblicitari né strumenti di statistica o di analisi del comportamento. Non è quindi presente alcun banner di consenso: gli strumenti impiegati sono esclusivamente tecnici e necessari, per i quali le Linee guida del Garante del 10 giugno 2021 non richiedono il consenso.",
        },
        {
          table: {
            head: ["Nome", "Tipo", "Funzione", "Durata"],
            rows: [
              ["session_token", "Cookie tecnico (httpOnly, Secure)", "Mantiene attiva la sessione dopo l'accesso", "Fino alla scadenza della sessione o alla disconnessione"],
              ["vs_token", "localStorage", "Copia del token di sessione, necessaria perché sito e API risiedono su domini diversi e alcuni browser bloccano i cookie di terze parti", "Fino alla disconnessione o alla pulizia del browser"],
              ["vs_lang", "localStorage", "Ricorda la lingua scelta per l'interfaccia", "Fino alla pulizia del browser"],
              ["visioscreen_defaults_v1", "localStorage", "Ricorda i dati predefiniti della struttura e del medico, per non doverli riscrivere a ogni screening", "Fino alla pulizia del browser"],
              ["vs_pending_report", "sessionStorage", "Conserva lo screening interrotto per il pagamento, così da poterlo riprendere", "Massimo 2 ore, comunque cancellato alla chiusura del browser"],
            ],
          },
        },
        {
          p: "Il pagamento avviene su una pagina ospitata da Stripe, che utilizza cookie propri, tecnici e antifrode, secondo la propria informativa. Le pagine pubbliche non caricano risorse da domini di terze parti: immagini, caratteri e script sono serviti dal nostro dominio, così la semplice visita non comunica il tuo indirizzo IP a nessun fornitore esterno. Puoi cancellare cookie e memoria locale in qualunque momento dalle impostazioni del browser; disattivare i cookie tecnici rende però impossibile l'accesso all'applicazione.",
        },
      ],
    },

    {
      id: "sicurezza",
      h: "9. Sicurezza",
      blocks: [
        {
          p: "Adottiamo misure tecniche e organizzative adeguate al rischio, ai sensi dell'art. 32 del GDPR, tra cui:",
        },
        {
          ul: [
            "trasmissione dei dati esclusivamente su connessioni cifrate (HTTPS/TLS);",
            "password conservate solo come hash bcrypt, mai in chiaro e mai reversibili;",
            "token di sessione in cookie httpOnly e Secure, con scadenza;",
            "isolamento dei dati per singolo account: nessun utente può accedere alla configurazione o ai referti di un altro;",
            "limitazione automatica dei tentativi di accesso ripetuti;",
            "indirizzi IP conservati solo in forma di hash SHA-256 con salt segreto, non riconducibile all'indirizzo originale senza il salt;",
            "riduzione all'origine dei dati trattati: i dati sanitari non lasciano il dispositivo del professionista.",
          ],
        },
        {
          p: "Nessuna misura di sicurezza può considerarsi assoluta. In caso di violazione dei dati personali che comporti un rischio per i diritti e le libertà degli interessati, provvederemo alla notifica al Garante entro 72 ore e, quando il rischio è elevato, alla comunicazione agli interessati, ai sensi degli artt. 33 e 34 del GDPR.",
        },
      ],
    },

    {
      id: "diritti",
      h: "10. I tuoi diritti",
      blocks: [
        { p: "In qualunque momento puoi esercitare i diritti riconosciuti dagli articoli da 15 a 22 del GDPR:" },
        {
          ul: [
            "accesso: ottenere conferma che sia in corso un trattamento e riceverne copia (art. 15);",
            "rettifica: correggere dati inesatti o integrare dati incompleti (art. 16);",
            "cancellazione: ottenere la rimozione dei dati che non siamo tenuti a conservare per legge (art. 17);",
            "limitazione: chiedere che il trattamento sia sospeso nei casi previsti (art. 18);",
            "portabilità: ricevere i dati che ci hai fornito in formato strutturato e leggibile da dispositivo automatico, o farli trasmettere a un altro titolare (art. 20);",
            "opposizione: opporti in qualsiasi momento ai trattamenti fondati sul legittimo interesse, per motivi legati alla tua situazione particolare (art. 21).",
          ],
        },
        {
          p: `Per esercitare questi diritti scrivi a ${CONTROLLER.email}${CONTROLLER.pec ? ` o, via posta elettronica certificata, a ${CONTROLLER.pec}` : ""}. Rispondiamo entro un mese dal ricevimento della richiesta, prorogabile di due mesi in caso di richieste particolarmente complesse, dandotene comunque notizia. L'esercizio dei diritti è gratuito, salvo richieste manifestamente infondate o eccessive.`,
        },
        {
          p: "Se ritieni che il trattamento dei tuoi dati violi il GDPR, hai diritto di proporre reclamo al Garante per la protezione dei dati personali — Piazza Venezia 11, 00187 Roma; telefono +39 06 696771; email garante@gpdp.it; PEC protocollo@pec.gpdp.it; www.garanteprivacy.it — o all'autorità di controllo dello Stato membro in cui risiedi. Resta impregiudicato il diritto di ricorrere all'autorità giudiziaria.",
        },
      ],
    },

    {
      id: "automatizzate",
      h: "11. Decisioni automatizzate e profilazione",
      blocks: [
        {
          p: "Non effettuiamo processi decisionali automatizzati né profilazione ai sensi dell'art. 22 del GDPR. Gli esiti dei test sono calcolati localmente sul dispositivo, hanno valore di supporto allo screening e non sostituiscono in alcun modo la valutazione e la diagnosi del professionista sanitario.",
        },
      ],
    },

    {
      id: "minori",
      h: "12. Minori",
      blocks: [
        {
          p: "Il Servizio si rivolge a professionisti nell'esercizio della propria attività e non è destinato a minori di età. Non raccogliamo consapevolmente dati di minori attraverso la registrazione all'account. Se ritieni che un minore abbia creato un account, segnalacelo e provvederemo alla cancellazione.",
        },
      ],
    },

    {
      id: "modifiche",
      h: "13. Modifiche a questa informativa",
      blocks: [
        {
          p: "Questa informativa può essere aggiornata per adeguarla a modifiche del Servizio, dei fornitori impiegati o della normativa applicabile. La versione vigente è sempre pubblicata su questa pagina, con l'indicazione della data di ultimo aggiornamento e del numero di versione. In caso di modifiche sostanziali, ne daremo avviso agli utenti registrati via email o all'interno dell'applicazione prima che diventino efficaci.",
        },
      ],
    },

    {
      id: "contatti",
      h: "14. Contatti",
      blocks: [
        {
          p: `Per qualunque domanda su questa informativa o sul trattamento dei tuoi dati personali puoi scrivere a ${CONTROLLER.email}${CONTROLLER.pec ? ` o a ${CONTROLLER.pec}` : ""}. È lo stesso indirizzo dell'assistenza, presidiato quotidianamente: le richieste relative ai dati personali vengono riconosciute e trattate come tali. Se ti occorre un recapito postale del titolare — per una raccomandata o per un reclamo formale — richiedilo a quell'indirizzo e ti viene fornito senza oneri.`,
        },
      ],
    },
  ],
};

export default doc;
